import Header from "../components/Header";
import { NavLink } from "react-router-dom";
import Empty from "../components/Empty";
import { useEffect, useState } from "react";
import Data from "../../Data";
import Error from "../components/Error";
import ukMoment from "../../../../utils/ukTime";
import Meta from "../../../components/Meta";

const PAGE_SIZE = 20;

const Bookings = () => {

    const [bookings, setBookings] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importUploadProgress, setImportUploadProgress] = useState(0);
    const [importResult, setImportResult] = useState(null);
    const [importError, setImportError] = useState(false);
    const [showOlderBookings, setShowOlderBookings] = useState(false);
    const [showStuckPayments, setShowStuckPayments] = useState(false);
    const [stuckPayments, setStuckPayments] = useState([]);
    const [stuckLoading, setStuckLoading] = useState(false);
    const [stuckError, setStuckError] = useState(false);
    const [recheckingId, setRecheckingId] = useState(null);

    const refreshBookings = async () => {
        try {
            setLoading(true);
            const result = await Data.fetchExperienceBookingsPage({ scope: showOlderBookings ? 'older' : 'upcoming', page, pageSize: PAGE_SIZE });
            setBookings(result.data.data);
            setTotalPages(result.data.totalPages);
        } catch (error) {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(async () => {
        await refreshBookings();
    }, [showOlderBookings, page]);

    const refreshStuckPayments = async () => {
        try {
            setStuckLoading(true);
            setStuckError(false);
            const result = await Data.fetchStuckExperienceBookingPayments(24);
            setStuckPayments(result.data);
        } catch (error) {
            setStuckError(true);
        } finally {
            setStuckLoading(false);
        }
    };

    useEffect(() => {
        if(!showStuckPayments) return;
        refreshStuckPayments();
    }, [showStuckPayments]);

    const recheckPayment = async (bookingId) => {
        try {
            setRecheckingId(bookingId);
            await Data.recheckExperienceBookingPayment(bookingId);
        } catch (error) {
            // surfaced implicitly by the row simply still showing up after refresh
        } finally {
            setRecheckingId(null);
            await refreshStuckPayments();
        }
    };

    const exportCsv = async () => {
        try {
            setExporting(true);
            setExportError(false);

            const response = await Data.exportExperienceBookings();
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'experience-bookings.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setExportError(true);
        } finally {
            setExporting(false);
        }
    };

    const importCsv = async (event) => {
        const file = event.target.files?.[0];
        if(!file) return;

        setImporting(true);
        setImportUploadProgress(0);
        setImportResult(null);
        setImportError(false);

        try {
            let finalResult = null;
            const response = await Data.importExperienceBookings(file, (progressEvent) => {
                if(!progressEvent.total) return;
                setImportUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            }, (progress) => {
                finalResult = progress;
                setImportResult(progress);
            });

            if(!finalResult && response.data) {
                const lines = String(response.data).trim().split('\n').filter(Boolean);
                if(lines.length) setImportResult(JSON.parse(lines[lines.length - 1]));
            }

            await refreshBookings();
        } catch (error) {
            setImportError(error?.response?.data?.message || "Unable to import bookings, please try again.");
        } finally {
            setImporting(false);
            event.target.value = "";
        }
    };

    const downloadFailedRecords = () => {
        if(!importResult?.failedRecords?.length) return;
        const headers = ['row', 'Start Time', 'End Time', 'First Name', 'Last Name', 'Phone', 'Email', 'Acuity Class Name', 'Surecode Class Name', 'message'];
        const escape = (value) => {
            if(value === null || value === undefined) return '';
            const text = String(value);
            return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        };
        const lines = importResult.failedRecords.map((failed) => {
            const record = failed.record || {};
            return headers.map((header) => escape(header === 'row' || header === 'message' ? failed[header] : record[header])).join(',');
        });
        const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'failed-experience-booking-import-records.csv');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const visibleBookings = bookings;

    return (
        <>
            <Meta title={"Bookings"} />
            <Header heading={"Bookings"} />
            <div className={"p-3 lg:p-6 grid grid-cols-1 gap-3 lg:gap-6 w-full"}>
                <div className={"flex flex-wrap gap-3"}>
                    <button
                        type="button"
                        onClick={() => { setShowOlderBookings(current => !current); setPage(1); }}
                        disabled={loading}
                        className={"bg-backgroundGrey px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}
                    >
                        {showOlderBookings ? "Show Upcoming Bookings" : "Show Older Bookings"}
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowStuckPayments(current => !current)}
                        className={"bg-backgroundGrey px-3 py-2 rounded-md hover:opacity-80 transition"}
                    >
                        {showStuckPayments ? "Hide Stuck Payments" : "Show Stuck Payments"}
                    </button>
                    <button
                        onClick={exportCsv}
                        disabled={loading || exporting}
                        className={"bg-primary text-white px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}
                    >
                        {exporting ? "Exporting..." : "Export Bookings"}
                    </button>
                    <label className={"bg-primary text-white px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}>
                        {importing ? "Importing..." : "Import Bookings"}
                        <input type="file" accept=".csv,text/csv" onChange={importCsv} disabled={importing} className={"hidden"} />
                    </label>
                </div>

                {error ? <Error message={"Unable to load bookings, please refresh the page."} /> : null}
                {exportError ? <Error message={"Unable to export bookings, please try again."} /> : null}
                {importError ? <Error message={importError} /> : null}

                {(importing || importResult) ? (
                    <div className={"bg-white border rounded-lg p-4 grid gap-2"}>
                        {importing ? (
                            <>
                                <p className={"text-textGrey"}>Upload progress: {importUploadProgress}%</p>
                                <progress value={importUploadProgress} max="100" className={"w-full"} />
                            </>
                        ) : null}
                        {importResult ? (
                            <>
                                <p>Stored: {importResult.stored} / {importResult.total}</p>
                                <p className={"text-textGrey"}>Processed: {importResult.processed} | Left: {importResult.remaining} | Failed: {importResult.skipped} | Users created: {importResult.createdUsers} | Events created: {importResult.createdEvents}</p>
                                {!!importResult.failedRecords?.length ? (
                                    <button
                                        type="button"
                                        onClick={downloadFailedRecords}
                                        className={"bg-backgroundGrey px-3 py-2 rounded-md hover:opacity-80 transition justify-self-start"}
                                    >
                                        Download Failed Records
                                    </button>
                                ) : null}
                            </>
                        ) : (
                            <p className={"text-textGrey"}>Processing starts after the CSV upload completes.</p>
                        )}
                    </div>
                ) : null}

                {showStuckPayments ? (
                    stuckError ? <Error message={"Unable to load stuck payments, please try again."} /> :
                    stuckLoading ?
                        <Empty>
                            <p>Loading...</p>
                        </Empty>
                    : stuckPayments.length === 0 ?
                        <Empty>
                            <p>No payments stuck in Pending/Failed longer than 24 hours.</p>
                        </Empty>
                    :
                        <div className={"w-full overflow-x-auto"} style={{ WebkitOverflowScrolling: 'touch' }}>
                        <table
                            className={"min-w-[900px] w-full border-collapse outline outline-1 outline-gray-200 rounded-lg overflow-hidden drop-shadow-sm"}>
                            <thead className={"bg-gray-50"}>
                            <tr>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Status</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Reference</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Customer</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Experience</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Event Date</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Booked On</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Actions</th>
                            </tr>
                            </thead>
                            <tbody className={"bg-white"}>
                            {stuckPayments.map((booking) => (
                                <tr key={booking._id} className={"border-t"}>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>
                                        <span className={"capitalize"}>{booking.status}</span>
                                    </td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{booking.reference}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>
                                        <p>{booking.customerName}</p>
                                        <p className={"text-textGrey text-sm"}>{booking.customerEmail}</p>
                                    </td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{booking.experienceName}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{booking.eventDate ? ukMoment(booking.eventDate).format("MMM Do YYYY, H:mm A") : '-'}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{ukMoment(booking.bookedOn).format("MMM Do YYYY, H:mm A")}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>
                                        <div className={"flex gap-2"}>
                                            <NavLink
                                                to={`/admin/class-bookings/${booking._id}`}
                                                className={"bg-backgroundGrey px-2.5 py-1 rounded hover:opacity-80 transition"}
                                            >
                                                View
                                            </NavLink>
                                            <button
                                                type="button"
                                                onClick={() => recheckPayment(booking._id)}
                                                disabled={recheckingId === booking._id}
                                                className={"bg-primary text-white px-2.5 py-1 rounded hover:opacity-80 transition disabled:opacity-60 disabled:cursor-not-allowed"}
                                            >
                                                {recheckingId === booking._id ? "Rechecking..." : "Recheck Payment"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        </div>
                ) : loading ?
                    <Empty>
                        <p>Loading...</p>
                    </Empty>
                    : visibleBookings.length === 0 ?
                        <Empty>
                            <p>{showOlderBookings ? "No Older Bookings" : "No Upcoming Bookings"}</p>
                        </Empty>
                        :

                        <>
                        <div className={"w-full overflow-x-auto"} style={{ WebkitOverflowScrolling: 'touch' }}>
                        <table
                            className={"min-w-[900px] w-full border-collapse outline outline-1 outline-gray-200 rounded-lg overflow-hidden drop-shadow-sm"}>
                            <thead className={"bg-gray-50"}>
                            <tr>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Status</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Reference</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Name</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Experience</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Date</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Tickets</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Booked On</th>
                                <th className={"text-left px-4 py-3 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>Actions</th>
                            </tr>
                            </thead>
                            <tbody className={"bg-white"}>
                            {visibleBookings.map((booking) => (
                                <tr key={booking._id} className={"border-t"}>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>
                                        <span className={"capitalize"}>{booking.status}</span>
                                        {booking.paymentVerificationStatus === 'mismatch' ? (
                                            <span
                                                title={booking.paymentVerificationReason || 'The payment provider has no matching successful payment on record.'}
                                                className={"ml-2 inline-block bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full align-middle"}
                                            >
                                                Payment not confirmed
                                            </span>
                                        ) : null}
                                    </td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{booking.reference}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{booking.User.firstName} {booking.User.lastName}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{booking.Event.Experience.name}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{ukMoment(booking.Event.date).format("MMM Do YYYY, H:mm A")}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{booking.tickets} people</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>{ukMoment(booking.bookedOn).format("MMM Do YYYY, H:mm A")}</td>
                                    <td className={"px-4 py-2 lg:px-6 lg:py-4 truncate overflow-ellipsis"}>
                                        <div className={"flex gap-2"}>
                                            <NavLink
                                                to={`/admin/class-bookings/${booking._id}`}
                                                className={"bg-backgroundGrey px-2.5 py-1 rounded hover:opacity-80 transition"}
                                            >
                                                View
                                            </NavLink>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        </div>

                        <div className={"flex items-center gap-2 justify-center"}>
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className={`px-2.5 py-1.5 rounded-md border ${page <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                Prev
                            </button>
                            <span className={"text-textGrey text-sm"}>Page {page} of {totalPages}</span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className={`px-2.5 py-1.5 rounded-md border ${page >= totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                Next
                            </button>
                        </div>
                        </>
                }
            </div>
        </>
    );
}

export default Bookings
